import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import { fetch_questions_in_project } from '@/services/question_service';
import {
  extract_question_choice_labels,
  normalize_question_type,
} from '@/services/risk_management_service';
import {
  ADD_QUESTIONS_TO_RISK_FRAMEWORK,
  GET_ASSIGNED_QUESTIONS_TO_ASSESSMENT,
  get_request_config,
  REMOVE_QUESTIONS_FROM_RISK_FRAMEWORK,
} from '@/utils/system_constants/constant.js';

export default {
  name: 'RiskQuestionnaire',
  props: {
    assessmentId: {
      type: [Number, String],
      default: null,
    },
  },
  mixins: [alert_mixin],
  data: () => ({
    search_query: '',
    current_page: 1,
    page_size: 10,
    questions_loading: false,
    assignments_loading: false,
    assignments_saving: false,
    selected_question_ids: [],
    unselected_question_ids: [],
    questions: [],
  }),
  computed: {
    filtered_questions() {
      const query = String(this.search_query || '').trim().toLowerCase();

      if (!query) return this.questions;

      return this.questions.filter((question) => [
        question.question,
        question.type,
        question.choices,
      ].join(' ').toLowerCase().includes(query));
    },
    paginated_questions() {
      const start = (this.current_page - 1) * this.page_size;
      return this.filtered_questions.slice(start, start + this.page_size);
    },
    total_pages() {
      return Math.max(1, Math.ceil(this.filtered_questions.length / this.page_size));
    },
    pagination_label() {
      if (!this.filtered_questions.length) return 'Showing 0 questions';

      const start = ((this.current_page - 1) * this.page_size) + 1;
      const end = Math.min(this.current_page * this.page_size, this.filtered_questions.length);
      return `Showing ${start} to ${end} of ${this.filtered_questions.length} questions`;
    },
    all_questions_selected() {
      return this.questions.length > 0
        && this.questions.every((question) => this.is_selected(question.id));
    },
    selected_count() {
      return this.selected_question_ids.length;
    },
  },
  watch: {
    search_query() {
      this.current_page = 1;
    },
    page_size() {
      this.current_page = 1;
    },
    assessmentId: {
      immediate: true,
      handler() {
        this.load_assigned_questions();
      },
    },
  },
  created() {
    this.load_questions();
  },
  methods: {
    normalize_question_id(value) {
      const question_id = Number(value);
      return Number.isFinite(question_id) && question_id > 0 ? question_id : null;
    },
    current_assessment_id() {
      const prop_value = Number(this.assessmentId);
      if (Number.isFinite(prop_value) && prop_value > 0) {
        return prop_value;
      }

      const route_query = this.$route?.query || {};
      const route_value = Number(route_query.risk_framework_assessment_id);

      return Number.isFinite(route_value) && route_value > 0 ? route_value : null;
    },
    build_question_request_payload() {
      const route_query = this.$route?.query || {};
      const payload = {};

      if (route_query.project_id) {
        payload.project_id = route_query.project_id;
      }

      if (route_query.form_id) {
        payload.form_id = route_query.form_id;
      }

      return payload;
    },
    build_question_row(question, index = 0) {
      const raw_type = question?.question_type?.type
        || question?.question_type?.question_type
        || question?.question_type
        || question?.type
        || '';

      const options = extract_question_choice_labels(question);

      return {
        id: question?.id ?? index + 1,
        question: String(question?.question_text || 'Untitled Question'),
        type: String(raw_type || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()).trim(),
        choices: options.length ? options.join(', ') : 'No options',
      };
    },
    async load_assigned_questions() {
      const assessment_id = this.current_assessment_id();
      if (!assessment_id) {
        this.selected_question_ids = [];
        return;
      }

      this.assignments_loading = true;

      try {
        const { data } = await axios.get(
          GET_ASSIGNED_QUESTIONS_TO_ASSESSMENT,
          get_request_config({ params: { assessment_id } }),
        );

        if (data?.status === 'success') {
          const rows = Array.isArray(data.data) ? data.data : [];
          this.selected_question_ids = rows
            .map((row) => this.normalize_question_id(
              row?.question_id ?? row?.question ?? row?.id,
            ))
            .filter((question_id) => question_id);
          return;
        }

        this.selected_question_ids = [];
      } catch (error) {
        this.selected_question_ids = [];
        this.show_error(error, 'Failed to load assigned questions.');
      } finally {
        this.assignments_loading = false;
      }
    },
    async load_questions() {
      this.questions_loading = true;

      try {
        const rows = await fetch_questions_in_project(this.build_question_request_payload());
        this.questions = rows
          .filter((question) => {
            const question_type = normalize_question_type(
            question?.question_type?.type
            || question?.question_type?.question_type
            || question?.question_type
            || question?.type,
          );

            return question_type.includes('multi_select') || question_type.includes('single_select');
          })
          .map((question, index) => this.build_question_row(question, index));
        this.current_page = 1;
        if (this.selected_question_ids.length) {
          const visible_ids = new Set(
            this.questions
              .map((question) => this.normalize_question_id(question.id))
              .filter((question_id) => question_id),
          );
          this.selected_question_ids = this.selected_question_ids.filter((question_id) => visible_ids.has(question_id));
        }
      } catch (error) {
        this.questions = [];
        this.show_error(error, 'Failed to load questions.');
      } finally {
        this.questions_loading = false;
      }
    },
    set_page(page) {
      if (page < 1 || page > this.total_pages) return;
      this.current_page = page;
    },
    is_selected(question_id) {
      const normalized_question_id = this.normalize_question_id(question_id);
      if (!normalized_question_id) {
        return false;
      }
      return this.selected_question_ids.includes(normalized_question_id);
    },
    async toggle_question(question_id) {
  const normalized_question_id = this.normalize_question_id(question_id);

  if (!normalized_question_id) {
    return;
  }

  const assessment_id = this.current_assessment_id();
  if (!assessment_id) {
    this.show_error('Missing risk framework assessment id.');
    return;
  }
  this.assignments_saving = true;
  try {
    if (this.is_selected(normalized_question_id)) {
      this.selected_question_ids = this.selected_question_ids.filter(
        (id) => id !== normalized_question_id
      );
      await axios.post(
        REMOVE_QUESTIONS_FROM_RISK_FRAMEWORK,
        {
          risk_framework_assessment_id: assessment_id,
          question_ids: [normalized_question_id],
        },
        get_request_config()
      );
      this.show_success('Question unassigned successfully.');
    } else {
      this.selected_question_ids = [
        ...this.selected_question_ids,
        normalized_question_id,
      ];
      await axios.post(
        ADD_QUESTIONS_TO_RISK_FRAMEWORK,
        {
          risk_framework_assessment_id: assessment_id,
          question_ids: [normalized_question_id],
        },
        get_request_config()
      );

      this.show_success('Question assigned successfully.');
    }

    await this.load_assigned_questions();
  } catch (error) {
    this.show_error(error, 'Failed to update question assignment.')
  } finally {
    this.assignments_saving = false;
  }
},
    select_all_questions() {
      this.selected_question_ids = this.questions
        .map((question) => this.normalize_question_id(question.id))
        .filter((question_id) => question_id);
    },
    clear_all_questions() {
      this.selected_question_ids = [];
    },
    async assign_selected_questions() {
      const assessment_id = this.current_assessment_id();
      if (!assessment_id) {
        this.show_error('Missing risk framework assessment id.');
        return;
      }

      if (!this.selected_question_ids.length) {
        this.show_error('Select at least one question to assign.');
        return;
      }

      this.assignments_saving = true;

      try {
        const payload = {
          risk_framework_assessment_id: assessment_id,
          question_ids: this.selected_question_ids,
        };

        const { data } = await axios.post(
          ADD_QUESTIONS_TO_RISK_FRAMEWORK,
          payload,
          get_request_config(),
        );

        if (data?.status === 'success') {
          this.show_success(data?.message || 'Questions assigned successfully.');
          await this.load_assigned_questions();
          return;
        }
        this.show_error(data?.message || 'Failed to assign selected questions.');
      } catch (error) {
        this.show_error(error, 'Failed to assign selected questions.');
      } finally {
        this.assignments_saving = false;
      }
    },
  },
};
